import { StudyPageContentEditor } from './StudyPageContentEditor';
import { useExperienceSettings } from '../useExperienceSettings';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ExperienceProfile, ExperienceProfileInput } from '@nava/backoffice-bff-contracts';
import { ExperienceProfileEditor } from './ExperienceProfileEditor';
import { ExperienceAssignments } from './ExperienceAssignments';
import { ExperienceProfileList } from './ExperienceProfileList';
import {
  publishExperience,
  saveExperienceDefaults,
  saveExperienceBinding,
  createExperienceProfile,
  updateExperienceProfile,
  archiveExperienceProfile,
} from '../api';

export function ExperienceSettingsContent({
  organizationId,
  study = null,
  onDirtyChange,
  onBusyChange,
}: {
  organizationId: string;
  study?: string | null;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const cache = useQueryClient();
  const key = ['participant-experience', organizationId];
  const [editing, setEditing] = useState<ExperienceProfile | 'new' | null>(null);
  const [editingSession, setEditingSession] = useState(0);
  const openEditor = (profile: ExperienceProfile | 'new') => {
    setEditingSession((current) => current + 1);
    setEditing(profile);
  };
  const [notice, setNotice] = useState('');
  const [dirty, setDirty] = useState(false);
  const [contentDirty, setContentDirty] = useState(false);
  const [contentBusy, setContentBusy] = useState(false);
  useEffect(() => onDirtyChange(dirty || contentDirty), [dirty, contentDirty, onDirtyChange]);
  const { profiles, defaults, binding } = useExperienceSettings(organizationId, study);
  const mutation = useMutation({
    mutationFn: async (action: () => Promise<unknown>) => action(),
    onMutate: () => setNotice(''),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: key });
      setNotice('Saved successfully.');
    },
  });
  useEffect(
    () => onBusyChange(mutation.isPending || contentBusy),
    [mutation.isPending, contentBusy, onBusyChange]
  );
  const discard = () => {
    if (!dirty || window.confirm('Discard unsaved profile changes?')) {
      setDirty(false);

      return true;
    }
    return false;
  };
  const save = (input: ExperienceProfileInput) =>
    mutation.mutate(async () => {
      const saved =
        editing && editing !== 'new'
          ? await updateExperienceProfile(organizationId, editing.id, {
              ...input,
              expectedRevision: editing.revision,
            })
          : await createExperienceProfile(organizationId, input);
      setEditing(saved);
    });
  const clone = (profile: ExperienceProfile) =>
    mutation.mutate(async () => {
      const copy = await createExperienceProfile(
        organizationId,
        {
          name: `${profile.name.slice(0, 130)} copy`,
          description: profile.description,
          overrides: {},
        },
        profile.id
      );
      openEditor(copy);
      if (study) await saveExperienceBinding(organizationId, study, copy.id);
    });
  const error = profiles.error || (study ? binding.error : defaults.error) || mutation.error;
  return (
    <div className="space-y-6">
      <p>
        Customize reusable participant wording for Insights and Customer Success.
        {study
          ? ' Edit this study’s own page texts in Study page content below.'
          : ' Clone a profile to create an independent wording variant.'}
      </p>
      {notice && <p role="status">{notice}</p>}
      {error && (
        <p role="alert" className="text-red-700">
          {error.message}
        </p>
      )}
      {profiles.isPending || (!study && defaults.isPending) || (!!study && binding.isPending) ? (
        <p>Loading profiles...</p>
      ) : (
        <>
          {!(profiles.error || (study ? binding.error : defaults.error)) && (
            <>
              <ExperienceAssignments
                profiles={profiles.data ?? []}
                defaults={study ? undefined : defaults.data}
                study={study}
                binding={binding.data}
                busy={mutation.isPending || contentBusy}
                onDefault={(line, id) =>
                  mutation.mutate(() =>
                    saveExperienceDefaults(organizationId, { ...defaults.data!, [line]: id })
                  )
                }
                onBinding={(id) =>
                  mutation.mutate(() => saveExperienceBinding(organizationId, study!, id))
                }
                publishDisabled={dirty || contentDirty}
                onPublish={() => mutation.mutate(() => publishExperience(organizationId, study!))}
              />
              <ExperienceProfileList
                profiles={profiles.data ?? []}
                busy={mutation.isPending || contentBusy}
                forStudy={!!study}
                onCreate={() => {
                  if (!discard()) return;
                  openEditor('new');
                  mutation.reset();
                  setNotice('');
                }}
                onEdit={(profile) => {
                  if (!discard()) return;
                  openEditor(profile);
                  mutation.reset();
                  setNotice('');
                }}
                onClone={(profile) => {
                  if (discard()) clone(profile);
                }}
                onArchive={(profile) =>
                  mutation.mutate(() => archiveExperienceProfile(organizationId, profile.id))
                }
              />
            </>
          )}
        </>
      )}
      {study && (
        <StudyPageContentEditor
          organizationId={organizationId}
          study={study}
          profile={profiles.data?.find((profile) => profile.id === binding.data?.profileId)}
          busy={mutation.isPending}
          onDirtyChange={setContentDirty}
          onBusyChange={setContentBusy}
        />
      )}
      {editing && (
        <ExperienceProfileEditor
          key={`${editingSession}:${editing === 'new' ? 'new' : `${editing.id}:${editing.revision}`}`}
          {...(editing === 'new' ? {} : { profile: editing })}
          busy={mutation.isPending || contentBusy}
          onSave={save}
          onDirtyChange={setDirty}
          onCancel={() => {
            if (discard()) setEditing(null);
          }}
        />
      )}
    </div>
  );
}
